import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import { Card, Row, Col, Badge, Button } from 'react-bootstrap';
import config from '../../config';
import { PulseLoader } from "react-spinners";
import ProductModal from '../../components/products/ProductModal';
import { toast } from 'react-toastify';

function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchProduct = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/products/${id}`);
      setProduct(res.data);
    } catch (err) {
      console.error('Failed to fetch product', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [id, fetchProduct]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.patch(`${config.API_BASE_URL}/products/${id}/deactivate`);
      toast.success('Product deleted successfully');
      navigate('/products');
    } catch (err) {
      console.error('Failed to delete product:', err);
      toast.error('Failed to delete product');
    }
  };

  if (isLoading) {
    return (
      <Layout title={product ? `Product: ${product.productName}` : "Product Details"}>
        <div className="d-flex justify-content-center">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout title={product ? `Product: ${product.productName}` : "Product Details"}>
        <div className="alert alert-danger">Product not found</div>
      </Layout>
    );
  }

  return (
    <Layout title={product ? `Product: ${product.productName}` : "Product Details"}>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/products">Products</Link></li>
          <li className="breadcrumb-item active">{product.productName}</li>
        </ol>
      </nav>

      <Row className="mb-3">
        <Col>
          <h2>{product.productName}</h2>
        </Col>
        <Col className="text-end">
          <div className="d-flex gap-2 justify-content-end">
            <Button 
              variant="primary" 
              onClick={() => setShowEditModal(true)}
            >
              Edit Product
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
            >
              Delete Product
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Basic Information</Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={4}><strong>Product Name:</strong></Col>
                <Col sm={8}>{product.productName}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Alias:</strong></Col>
                <Col sm={8}>{product.alias}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>HSN Code:</strong></Col>
                <Col sm={8}>{product.productCode}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>SKU:</strong></Col>
                <Col sm={8}>{product.sku}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Category:</strong></Col>
                <Col sm={8}>{product.category}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Status:</strong></Col>
                <Col sm={8}>
                  <Badge bg={product.isActive ? 'success' : 'danger'}>
                    {product.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Additional Details</Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={4}><strong>Unit:</strong></Col>
                <Col sm={8}>
                  {product.unitId ? `${product.unitId.unitName} (${product.unitId.unitCode})` : 'N/A'}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Group:</strong></Col>
                <Col sm={8}>
                  {product.groupId ? `${product.groupId.groupName} (${product.groupId.groupCode})` : 'N/A'}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Buffer Stock:</strong></Col>
                <Col sm={8}>{product.bufferStock || 0} {product.unitId?.unitCode}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>GST:</strong></Col>
                <Col sm={8}>{product.gst}%</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>CESS:</strong></Col>
                <Col sm={8}>{product.cess}%</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>Created:</strong></Col>
                <Col sm={8}>{new Date(product.createdAt).toLocaleDateString()}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ProductModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        product={product}
        onUpdated={fetchProduct}
      />
    </Layout>
  );
}

export default ProductDetails;
